import React, { createContext, useRef, useState, useEffect } from "react";

// Create Context
export const InterviewContext = createContext();

const InterviewProvider = ({ children }) => {
  const videoStartTimeRef = useRef(null); // To track when the video starts recording
  const [botTimestamps, setBotTimestamps] = useState([]); // To store bot speech timestamps
  const [interviewStatus, setInterviewStatus] = useState(null); // Track interview status
  const [elapsedTime, setElapsedTime] = useState(0); // Track elapsed time
  const [isRecording, setIsRecording] = useState(false); // Track if recording is active
  const [convertedVideoUrl, setConvertedVideoUrl] = useState(null); // MP4 URL
  const [chatHistory, setChatHistory] = useState([]); // Chat history
  const [botStatus, setBotStatus] = useState(null); // Bot status
  const abortControllerRef = useRef(null);
  const [isBlurBackgroundEnabled, setIsBlurBackgroundEnabled] = useState(false);
  const [blurBackgroundState, setBlurBackgroundState] = useState(false);

  const createAbortController = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort(); // Abort any ongoing requests
    }
    abortControllerRef.current = new AbortController(); // Create new controller
    return abortControllerRef.current;
  };

  const getAbortSignal = () => {
    if (!abortControllerRef.current) {
      createAbortController(); // Ensure a controller exists
    }
    return abortControllerRef.current.signal;
  };

  const abortRequest = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort(); // Abort the request
    }
  };

  // Start Interview
  const startInterview = () => {
    setInterviewStatus("start");
  };

  const startVideoRecording = () => {
    videoStartTimeRef.current = Date.now();
    setIsRecording(true);
  };

  const stopVideoRecording = () => {
    videoStartTimeRef.current = null;
    setIsRecording(false);
  };

  // Pause Interview
  const pauseInterview = () => {
    setInterviewStatus("paused");
  };

  // Resume Interview
  const resumeInterview = () => {
    setInterviewStatus("ongoing");
  };

  // Complete Interview
  const completeInterview = () => {
    setInterviewStatus("done");
  };

  // Add Bot Timestamp
  const addBotTimestamp = (
    botMessage,
    baseEncodedAudio = null,
    botStatus,
    type = null,
    time = null
  ) => {
    let elapsedTime = 0;
    if (videoStartTimeRef.current) {
      elapsedTime = Date.now() - videoStartTimeRef.current;
    }

    setBotTimestamps((prev) => [
      ...prev,
      {
        time: time || elapsedTime,
        text: botMessage,
        baseEncodedAudio: baseEncodedAudio,
        botStatus: botStatus,
        type,
      },
    ]);
  };

  const getElapsedTime = () => {
    return Date.now() - videoStartTimeRef.current;
  };

  const resetInterview = () => {
    setInterviewStatus(null);
    setBotTimestamps([]);
    videoStartTimeRef.current = null;
    setElapsedTime(0);
    setIsRecording(false);
    setConvertedVideoUrl(null);
    setChatHistory([]);
  };

  const downloadBotTimestampsJSON = () => {
    const element = document.createElement("a");
    const file = new Blob([JSON.stringify(botTimestamps)], {
      type: "application/json",
    });
    element.href = URL.createObjectURL(file);
    element.download = "botTimestamps.json";
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();
  };

  // Update elapsed time every second
  useEffect(() => {
    let timer;
    if (isRecording && interviewStatus === "start") {
      timer = setInterval(() => {
        if (videoStartTimeRef.current) {
          setElapsedTime(Date.now() - videoStartTimeRef.current);
        }
      }, 1000);
    } else {
      clearInterval(timer);
    }
    return () => clearInterval(timer);
  }, [isRecording, interviewStatus]);

  // Format elapsed time as mm:ss
  const formatElapsedTime = (time) => {
    const minutes = Math.floor(time / 60000);
    const seconds = Math.floor((time % 60000) / 1000);
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  return (
    <InterviewContext.Provider
      value={{
        videoStartTimeRef,
        botTimestamps,
        setBotTimestamps,
        interviewStatus,
        startInterview,
        pauseInterview,
        resumeInterview,
        completeInterview,
        addBotTimestamp,
        resetInterview,
        setInterviewStatus,
        startVideoRecording,
        stopVideoRecording,
        setBotTimestamps,
        downloadBotTimestampsJSON,
        elapsedTime: formatElapsedTime(elapsedTime),
        setConvertedVideoUrl,
        convertedVideoUrl,
        chatHistory,
        setChatHistory,
        createAbortController,
        getAbortSignal,
        abortRequest,
        botStatus,
        setBotStatus,
        getElapsedTime,
        isBlurBackgroundEnabled,
        setIsBlurBackgroundEnabled,
        setBlurBackgroundState,
        blurBackgroundState,
      }}
    >
      {children}
    </InterviewContext.Provider>
  );
};

export default InterviewProvider;
